/* eslint-disable @typescript-eslint/no-unused-vars */
import { Box, LinearProgress, Stack } from "@mui/material";
import { useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDialogs } from "../../../../components";
import { FormProvider, FormState } from "../../../../components/FormProvider";
import { Child } from "../../../../models";
import { useDeleteChildMutation, useGetChildByIdQuery, useSaveChildMutation } from "../../../../redux/apiStore";
import { ChildDetailAppBarSetup } from "./ChildDetailAppBarSetup";
import { EditChildForm } from "./EditChildForm";

export function ChildDetailPage() {
  const params = useParams();
  const childId = parseInt(params.childId || "");
  const getChildById = useGetChildByIdQuery({ id: childId });
  const [saveChild, saveChildStatus] = useSaveChildMutation();
  const [deleteChild, deleteChildStatus] = useDeleteChildMutation();
  const isLoading = saveChildStatus.isLoading || getChildById.isFetching;
  const formDataGetterRef = useRef<(() => Child | null) | null>(null);
  const resetFormRef = useRef<(() => void) | null>(null);
  const navigate = useNavigate();
  const dialogs = useDialogs();

  const handleSave = async (form: FormState) => {
    const child = formDataGetterRef.current ? formDataGetterRef.current() : null;
    form.dismissAlert();
    if (!child) {
      form.notifyError("Failed to save child. Child is null for some reason");
      return;
    }
    try {
      const result = await saveChild(child).unwrap();
      form.notifySuccess("Child saved");
    } catch (err) {
      form.notifyError("Error saving child", err);
    }
  };

  const handleDelete = async (form: FormState) => {
    const child = formDataGetterRef.current ? formDataGetterRef.current() : null;
    form.dismissAlert();
    if (!child) {
      form.notifyError("Failed to delete child. Child is null for some reason");
      return;
    }
    try {
      const confirmed = await dialogs.confirm(`Are you sure you want to delete ${child.firstName} ${child.lastName}?`);
      if (confirmed) {
        const result = await deleteChild({ id: child.id }).unwrap();
        form.notifySuccess("Child deleted");
        navigate(`/families/${child?.familyId}`);
      } else {
        console.log("Child not deleted");
      }
    } catch (err) {
      form.notifyError("Error deleting child", err);
    }
  };

  const handleCancel = async (form: FormState) => {
    try {
      const confirmed = await dialogs.confirm("Are you sure you want to undo your changes?");
      if (confirmed) {
        if (resetFormRef.current) {
          resetFormRef.current();
        } else {
          console.error("Failed to undo form changes. No form reset method has been set.");
        }
      }
    } catch (err) {
      form.notifyError("Error undoing changes", err);
    }
  };

  return (
    <Stack direction="column" sx={{ p: 2, flexGrow: 1 }}>
      <FormProvider fullHeight onSave={handleSave} onCancel={handleCancel} onDelete={handleDelete}>
        <Stack flexGrow={1}>
          {isLoading ? <LinearProgress /> : <Box sx={{ height: 4 }} />}

          <ChildDetailAppBarSetup />

          {getChildById?.data && <EditChildForm child={getChildById?.data} dataGetterRef={formDataGetterRef} resetFormRef={resetFormRef} />}
        </Stack>
      </FormProvider>
    </Stack>
  );
}
